import { View, ScrollView, Platform, Linking } from 'react-native';
import { Pressable } from 'react-native';
import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import styles from '@stylesheet';
import { H5, H4, SizableText, Text, XStack, YStack, Spinner, H6 } from 'tamagui';
import { Title, Card } from '@constants/Themed';
import handleImage from './handleImage';
import * as Animatable from 'react-native-animatable';
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import FlipCard from 'react-native-flip-card';
import apis, { connectPlatformCallback } from '@api/api';
import Button from '@components/UI/Button/Button';
import AnimatedLottieView from '@utils/lottieAnimation';
import { useDispatch, useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { selectUser } from '@reduxLocal/selectors/userSelectors';
import RowComponent from '@components/Global/RowComponent/RowComponent';
import { scopes } from '@utils/scopeMap';
import { ADD_INTEGRATION, HIDE_LOADING_MODAL, SHOW_AUTH_MODAL, SHOW_LOADING_MODAL } from '@reduxLocal/actions/types';
import { set } from 'lodash';
import * as WebBrowser from "expo-web-browser";
import { useAuthRequest, makeRedirectUri, ResponseType } from 'expo-auth-session';
import { createURL } from 'expo-linking';
import { authCallback } from '@api/api';
import FacebookWhatsAppSignup from './subcomponents/whatsappSignup';
import { useDeviceType } from '@hooks/useDeviceType';
import { useQueryClient } from '@tanstack/react-query';
import { useAlertDialog } from '@components/Global/AlertDialog/useAlertDialog';
import WhatsappOptions from './subcomponents/integrationOptions/WhatsappOptions';
import YoutubeOptions from './subcomponents/integrationOptions/YoutubeOptions';
import StripeOptions from './subcomponents/integrationOptions/StripeOptions';
import XOptions from './subcomponents/integrationOptions/XOptions';
import LinkedInOptions from './subcomponents/integrationOptions/LinkedInOptions';
import ShopifyOptions from './subcomponents/integrationOptions/ShopifyOptions';
import GenerateSchemaForm from '@hooks/generateSchemaForm';
import WordpressOptions from './subcomponents/integrationOptions/WordpressOptions';
import InstagramOptions from './subcomponents/integrationOptions/InstagramOptions';
import SlackOptions from './subcomponents/integrationOptions/SlackOptions';
import LovableOptions from './subcomponents/integrationOptions/LovableOptions';
import HuggingFaceOptions from './subcomponents/integrationOptions/HuggingFaceOptions';
import PayStackOptions from './subcomponents/integrationOptions/PayStackOptions';
import ErrorBoundary from '@utils/ErrorBoundary';
import * as AuthSession from 'expo-auth-session';
import { backgroundColor } from 'react-native-calendars/src/style';
import { useTranslation } from 'react-i18next';
import FacebookOptions from './subcomponents/integrationOptions/FacebookOptions';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { IIntegration } from "@types/integrationTypes";
import SpecDrivenIntegrationRenderer from './SpecDrivenIntegrationRenderer';

// Create option list as a function that takes integration
const getOptionList = (integration: IntegrationProps) => [
  {
    title: "wordpress",
    component: <WordpressOptions />
  },
  {
    title: "linkedin",
    component: <LinkedInOptions />
  },
  {
  title: "whatsapp",
  component: <WhatsappOptions />
  },
  {
    title:'stripe',
    component: <StripeOptions />
  },
  {
    title:'x',
    component: <XOptions integration={integration} />
  },
  {
    title: 'slack',
    component: <SlackOptions />
  },
  {
    title: "facebook",
    component: <FacebookOptions />
  },
  {
    title: "instagram",
    component: <InstagramOptions />
  },
  {
    title:"youtube",
    component: <YoutubeOptions integration={integration} />
  },
  {
    title: "shopify",
    component: <ShopifyOptions />
  },
  {
    title: "lovable",
    component: <LovableOptions />
  },
  {
    title: "huggingface",
    component: <HuggingFaceOptions />
  },
  {
    title: "paystack",
    component: <PayStackOptions />
  }
]

interface IntegrationProps {
  title: string,
  meta: string,
  description: string,
  extendedDescription?: string,
  logo?: string,
  endPoint?: string,
  apiUrl?: string,
  themeColor?: string,
  clientId?: string,
  clientSecret?: string,
  clientKey?: string,
  scopes?: string[],
}

const IntegrationComponent = ({ integration }: { integration: IntegrationProps }) => {
  const [flip, setFlip] = useState(false);
  const [servRes, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const { authentications } = selectUser();
  const dispatch = useDispatch();
  const { isTablet } = useDeviceType();
  const queryClient = useQueryClient();
  const { showConfirm } = useAlertDialog();
  const { t } = useTranslation();
  const theme = themeSelector();

  // Memoize sources array to ensure it updates when integration changes
  const sources = useMemo(() => {
    const sourceArray = [];
    if (integration.meta) {
      const slug = integration.meta.toLowerCase().replace(/ /g, '');
      sourceArray.push({ uri: `https://cdn.simpleicons.org/${slug}` });
    }
    if (integration.logo) {
      sourceArray.push({ uri: integration.logo });
    }
    sourceArray.push(handleImage(integration.meta));
    return sourceArray;
  }, [integration.meta, integration.logo]);

  // Theme colors
  const colors = {
    background: theme ? '#ffffff' : '#374151',
    cardBackground: theme ? '#f8fafc' : '#4b5563',
    text: theme ? '#1f2937' : '#f9fafb',
    textSecondary: theme ? '#6b7280' : '#9ca3af',
    border: theme ? '#e5e7eb' : '#4b5563',
    accent: '#3b82f6',
    success: '#10b981',
    warning: '#f59e0b',
    danger: '#ef4444',
    smartBlue: 'rgb(90,192,234)',
  };

  // Check if Hugging Face is connected (uses API token, not OAuth)
  const isHuggingFace = integration.meta?.toLowerCase().replace('-', '') === 'huggingface' || integration.title?.toLowerCase().replace(/\s+/g, '') === 'huggingface';
  // Check if PayStack is connected (uses API keys, not OAuth)
  const isPayStack = integration.meta?.toLowerCase().replace('-', '') === 'paystack' || integration.title?.toLowerCase().replace(/\s+/g, '') === 'paystack';
  const isConnected = isHuggingFace
    ? authentications?.huggingface?.token || authentications?.huggingface?.username
    : isPayStack
    ? authentications?.paystack?.keysStored || authentications?.paystack?.merchantEmail
    : authentications?.[integration.title.toLowerCase()];

  const handleRemoveIntegration = useCallback(async () => {
    const res = await showConfirm(`Are you sure you want to remove ${integration.title} integration?`, `This will remove all data associated with this integration.`, { cancelText: 'Cancel', confirmText: 'Remove' });
    if (res) {
      if (isHuggingFace) {
        // For Hugging Face, use the specific remove token endpoint
        try {
          dispatch({ type: SHOW_LOADING_MODAL });
          await apis.removeHuggingFaceToken();
          dispatch({ type: HIDE_LOADING_MODAL });
          dispatch({ type: SHOW_AUTH_MODAL, data: { content: `${integration.title} integration removed successfully`, type: 'success' } });
          queryClient.invalidateQueries({ queryKey: ['integrationData'] });
        } catch (error) {
          dispatch({ type: HIDE_LOADING_MODAL });
          dispatch({ type: SHOW_AUTH_MODAL, data: { content: `Failed to remove ${integration.title}: ${error.message}`, type: 'error' } });
        }
      } else if (isPayStack) {
        // For PayStack, use the specific remove keys endpoint
        try {
          dispatch({ type: SHOW_LOADING_MODAL });
          await apis.removePayStackKeys();
          dispatch({ type: HIDE_LOADING_MODAL });
          dispatch({ type: SHOW_AUTH_MODAL, data: { content: `${integration.title} integration removed successfully`, type: 'success' } });
          queryClient.invalidateQueries({ queryKey: ['integrationData'] });
        } catch (error) {
          dispatch({ type: HIDE_LOADING_MODAL });
          dispatch({ type: SHOW_AUTH_MODAL, data: { content: `Failed to remove ${integration.title}: ${error.message}`, type: 'error' } });
        }
      } else {
        await apis.removeIntegration(integration.meta);
        dispatch({ type: HIDE_LOADING_MODAL });
        dispatch({ type: SHOW_AUTH_MODAL, data: { content: `${integration.title} integration removed successfully`, type: 'success' } });
      }
    }
  }, [integration, isHuggingFace, isPayStack, dispatch, showConfirm, queryClient])

  const excludeList = ['whatsapp', 'facebook', 'instagram', 'wordpress', 'yodeck', 'lovable', 'huggingface', 'hugging-face', 'paystack'];

  const site = integration.endPoint;

  const discovery = { authorizationEndpoint: site }

  const redirectUriForAuth = Platform.select({ web: createURL('/universal/integrations'), default: `https://api.smartsaas.pro/app-redirect` });

  // Log redirect URI for Xero debugging
  if (integration.meta === 'xero') {
    console.log('[IntegrationComponent - Xero]: Authorization setup:', {
      redirectUri: redirectUriForAuth,
      redirectUriLength: redirectUriForAuth?.length || 0,
      redirectUriExact: JSON.stringify(redirectUriForAuth),
      clientId: integration.clientId ? `${integration.clientId.substring(0, 10)}...` : 'missing',
      hasClientSecret: !!integration.clientSecret,
      scopes: integration.scopes,
      expectedRegisteredUri: 'https://localhost:19006/universal/integrations',
      matchesRegistered: redirectUriForAuth === 'https://localhost:19006/universal/integrations'
    });
  }

  const [request, response, promptAsync] = useAuthRequest(
    {
      clientId: integration.clientId,
      scopes: integration.scopes,
      ...(integration.meta !== 'youtube' && { clientSecret: integration.clientSecret }),
      redirectUri: redirectUriForAuth,
      responseType: ResponseType.Code,
      extraParams: {
        client_key: integration.clientKey || integration.clientId,
      }
    },
    discovery
  );

  // Log the actual request object for Xero after it's created
  React.useEffect(() => {
    if (integration.meta === 'xero' && request) {
      console.log('[IntegrationComponent - Xero]: Request object created:', {
        redirectUri: request.redirectUri,
        redirectUriLength: request.redirectUri?.length || 0,
        redirectUriExact: JSON.stringify(request.redirectUri),
        hasCodeVerifier: !!request.codeVerifier,
        clientId: request.clientId ? `${request.clientId.substring(0, 10)}...` : 'missing'
      });
    }
  }, [request, integration.meta]);

  // Debug: Show passed params when promptAsync is called
  const debugPromptAsync = async (params?: any) => {
    const paramsToLog = {
      clientId: integration.clientId,
      scopes: integration.scopes,
      // Only include clientSecret if not youtube
      ...(integration.meta !== 'youtube' && { clientSecret: integration.clientSecret }),
      redirectUri: Platform.select({ web: createURL('/universal/integrations'), default: `https://api.smartsaas.pro/app-redirect` }),
      responseType: ResponseType.Code,
      extraParams: {
        client_key: integration.clientKey,
      }
    };
    console.log('promptAsync called with params:', params, 'with params:', paramsToLog);
    return promptAsync(params);
  };

  const handleGotCode = async (code, verifier?: string) => {
    try {
      dispatch({ type: SHOW_LOADING_MODAL });
      const res = await connectPlatformCallback(code, integration.meta, request.codeVerifier, verifier);
      dispatch({ type: ADD_INTEGRATION, payload: { authType: integration.meta, data: res.data } });
      dispatch({ type: HIDE_LOADING_MODAL });
      dispatch({ type: SHOW_AUTH_MODAL, data: { content: res.message, type: 'success' } });

      // Refresh integration data
      queryClient.invalidateQueries({ queryKey: ['integrationData'] });
      queryClient.refetchQueries({ queryKey: ['integrationData'] });
    } catch (error) {
      console.error('Integration connection error:', error);
      dispatch({ type: HIDE_LOADING_MODAL });
      dispatch({ type: SHOW_AUTH_MODAL, data: { content: `Failed to connect ${integration.title}: ${error.message}`, type: 'error' } });
    }
  }

  useEffect(() => {
    dispatch({ type: HIDE_LOADING_MODAL })
    if (response?.type === 'success') {
      const { code } = response.params;
      // Pass redirectUri to ensure it matches what was used in authorization request
      const redirectUri = request?.redirectUri;
      console.log('[IntegrationComponent] OAuth callback:', {
        integration: integration.meta,
        code: code ? `${code.substring(0, 10)}...` : 'missing',
        redirectUri: redirectUri || 'missing',
        redirectUriLength: redirectUri?.length || 0
      });
      handleGotCode(code, redirectUri);
    }
  }, [response]);

  const handleFlip = async () => {
    if (integration.apiUrl) {
      await apis.getIntegrationApiAction(integration.apiUrl).then((res) => {
        setLoading(false);
        setResponse(res.data);
      })
    }
  }

  const fbLoginCallback = (response, redirectUri?: string) => {
    if (response.authResponse) {
      const code = response.authResponse.code;
      // The returned code must be transmitted to your backend first and then
      // perform a server-to-server call from there to our servers for an access token.
      handleGotCode(code, redirectUri);
    } else if (response.error) {
      console.error('Facebook login error:', response.error);
      dispatch({ type: SHOW_AUTH_MODAL, data: { content: `Facebook login failed: ${response.error}`, type: "error" } });
    } else {
      console.log('Facebook login cancelled');
      dispatch({ type: SHOW_AUTH_MODAL, data: { content: 'Facebook login was cancelled', type: "warning" } });
    }
  }

  const launchWhatsAppSignup = () => {
    const redirectUri = Platform.select({
      web: encodeURIComponent(createURL('/universal/integrations')),
      default: encodeURIComponent('https://api.smartsaas.pro/app-redirect')
    });

    // Launch Facebook login
    if (Platform.OS === 'web') {
      FB.login(fbLoginCallback, {
        config_id: '940039101343114', // configuration ID goes here
        response_type: 'code', // must be set to 'code' for System User access token
        override_default_response_type: true, // when true, any response types passed in the "response_type" will take precedence over the default types
        redirect_uri: redirectUri, // redirect URI for the user to be redirected to after login
        extras: {
          setup: {},
          featureType: '',
          sessionInfoVersion: '2',
        }
      });
    } else {
      promptAsync({
        config_id: '940039101343114', // configuration ID goes here
        response_type: 'code', // must be set to 'code' for System User access token
        override_default_response_type: true, // when true, any response types passed in the "response_type" will take precedence over the default types
        redirect_uri: redirectUri, // redirect URI for the user to be redirected to after login
        extras: {
          config_id: '940039101343114',
          setup: {},
          featureType: '',
          sessionInfoVersion: '2',
        }
      })
    }
  }

  //BOTH INSTAGRAM AND FACEBOOK
  const launchInstagramSignup = () => {
    const redirectUri = Platform.select({
      web: createURL('/universal/integrations'),
      default: 'https://api.smartsaas.pro/app-redirect'
    });

    if (Platform.OS === 'web') {
      FB.login((response) => fbLoginCallback(response, redirectUri), {
        config_id: '942548817203964',
        response_type: 'code',
        override_default_response_type: true,
        redirect_uri: redirectUri,
        extras: {
          setup: {},
          featureType: '',
          sessionInfoVersion: '2',
        }
      });
    } else {
      // For mobile, use promptAsync with Instagram config
      promptAsync({
        config_id: '942548817203964',
        response_type: 'code',
        override_default_response_type: true,
        extraParams: {
          redirect_uri: redirectUri,
          config_id: '942548817203964',
          setup: {},
          featureType: '',
          sessionInfoVersion: '2',
        }
      });
    }
  }

  // const handleMeta = (meta) => {
  //   if (meta === 'whatsapp') {
  //     return <FacebookWhatsAppSignup />
  //   }
  // }

  return (
    <YStack
      flex={1}
      backgroundColor={colors.background}
      borderRadius={12}
      borderWidth={1}
      top={10}
      borderColor={colors.border}
      overflow="hidden"
      shadowColor="#000"
      shadowOffset={{ width: 0, height: 2 }}
      shadowOpacity={0.1}
      shadowRadius={4}
    >
      {
        isConnected ?
          <YStack flex={1}>
            {/* Header Section - Compact Mobile-Friendly */}
            <XStack
              position="relative"
              backgroundColor="transparent"
              paddingHorizontal="$3"
              paddingVertical="$2"
              alignItems="center"
              gap="$2"
              borderRadius={12}
              borderEndStartRadius={0}
              borderEndEndRadius={0}
              overflow="hidden"
              style={{
                // Glass morphism effect
                backgroundColor: Platform.OS === 'web' ? 'rgba(200, 162, 200, 0.15)' : 'rgba(200, 162, 200, 0.25)',
                ...(Platform.OS === 'web' && {
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                }),
                borderWidth: 1,
                borderColor: 'rgba(255, 255, 255, 0.2)',
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: 0.1,
                shadowRadius: 4,
                maxHeight: Platform.OS === 'web' ? 50 : 48,
                minHeight: Platform.OS === 'web' ? 50 : 48,
              }}
            >
              {/* Gradient Background Layer */}
              {Platform.OS === 'web' ? (
                <View
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(135deg, rgba(200, 162, 200, 0.15) 0%, rgba(135, 206, 235, 0.15) 100%)',
                    zIndex: 0,
                  }}
                />
              ) : (
                <LinearGradient
                    colors={['rgba(200, 162, 200, 0.15)', '#87CEEB']}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 1 }}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 0,
                  }}
                />
              )}

              {/* Icon Container - Smaller */}
              <View style={{
                width: Platform.OS === 'web' ? 36 : 32,
                height: Platform.OS === 'web' ? 36 : 32,
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                borderRadius: Platform.OS === 'web' ? 18 : 16,
                justifyContent: 'center',
                alignItems: 'center',
                overflow: 'hidden',
                borderWidth: 1.5,
                borderColor: 'rgba(255, 255, 255, 0.3)',
                zIndex: 1,
              }}>
                <Image
                  key={`${integration.meta}-${integration.title}-connected`}
                  source={sources}
                  contentFit='contain'
                  style={{ width: Platform.OS === 'web' ? 28 : 24, height: Platform.OS === 'web' ? 28 : 24 }}
                />
              </View>

              {/* Title and Status - Compact */}
              <XStack alignSelf="stretch" flex={1} zIndex={1} alignItems="center" gap="$2">
                <H4
                  flex={1}
                  fontWeight="600"
                  fontSize={Platform.OS === 'web' ? "$5" : "$4"}
                  numberOfLines={1}
                  ellipsizeMode="tail"
                  color="grey"
                >
                  {integration.title}
                </H4>
                <XStack gap="$1.5" alignItems="center" flexShrink={0}>
                  <MaterialCommunityIcons
                    name="check-circle"
                    size={Platform.OS === 'web' ? 16 : 14}
                    color="#10b981"
                  />
                  <SizableText
                    color="rgba(255,255,255,0.95)"
                    size={Platform.OS === 'web' ? "$3" : "$2"}
                    fontWeight="600"
                    numberOfLines={1}
                  >
                    Connected
                  </SizableText>
                </XStack>
              </XStack>
            </XStack>

            {/* Content Section */}
            <ScrollView
              style={{ flex: 1 }}
              contentContainerStyle={{
                padding: Platform.OS === 'web' ? 20 : 12,
                gap: Platform.OS === 'web' ? 16 : 12,
              }}
              showsVerticalScrollIndicator={false}
            >
              <ErrorBoundary>
                {/* Check if integration has enriched OpenAPI spec - use spec-driven rendering */}
                {(integration as any).openAPISpec?.enrichedSpec ? (
                  (() => {
                    const integrationId = (integration as any)._id || (integration as any).id || integration.meta;
                    if (__DEV__) {
                      console.log('🎨 Rendering spec-driven integration:', integrationId, {
                        hasEnrichedSpec: !!(integration as any).openAPISpec?.enrichedSpec,
                        spec: (integration as any).openAPISpec?.enrichedSpec
                      });
                    }
                    return (
                      <SpecDrivenIntegrationRenderer
                        integrationId={integrationId}
                      />
                    );
                  })()
                ) : (
                  // Fallback to legacy connector-specific components
                  getOptionList(integration).find(item => {
                    const normalizedMeta = integration.meta?.toLowerCase().replace('-', '');
                    const normalizedTitle = item.title?.toLowerCase();
                    return normalizedTitle === normalizedMeta;
                  })?.component || null
                )}
              </ErrorBoundary>

              {/* Scopes Section */}
              {authentications?.[integration.title.toLowerCase()]?.scopes &&
                Array.from(new Set(authentications[integration.title.toLowerCase()].scopes))
                  .map((scope, index) => {
                    if (scope !== 'email' && scope !== 'profile') {
                      return scopes[integration.title.toLowerCase()] && scopes[integration.title.toLowerCase()][scope as string] && (
                        <View
                          key={String(scope)}
                          style={{
                            backgroundColor: colors.cardBackground,
                            padding: 16,
                            borderRadius: 12,
                            borderWidth: 1,
                            borderColor: colors.border,
                          }}
                        >
                          <RowComponent
                            buttonText='Remove Permission'
                            buttonColor='amber'
                            onPress={async () => await apis.revokeGoogleScope(scope as string)}
                            caution
                            style={{ flex: 1 }}
                            subtitle={scopes[integration.title.toLowerCase()][scope as string]?.description || ""}
                            title={scopes[integration.title.toLowerCase()][scope as string]?.title || ""}
                          />
                        </View>
                      )
                    }
                    return null;
                  })
              }

              {/* Remove Integration Section */}
              <View
                style={{
                  backgroundColor: 'rgba(239, 68, 68, 0.05)',
                  padding: 16,
                  borderRadius: 12,
                  borderWidth: 1,
                  borderColor: 'rgba(239, 68, 68, 0.2)',
                  marginTop: 8,
                }}
              >
                <RowComponent
                  buttonText='Remove Integration'
                  buttonColor='red'
                  forceColumn={Platform.OS != 'web'}
                  onPress={handleRemoveIntegration}
                  caution
                  style={{ flex: 1 }}
                  subtitle={`Remove ${integration.title} integration`}
                  title={`Remove ${integration.title}`}
                />
              </View>
            </ScrollView>
          </YStack>
          :
          <YStack flex={1}>
            {/* Header Section - Compact Mobile-Friendly */}
            <XStack
              position="relative"
              backgroundColor="transparent"
              paddingHorizontal="$3"
              paddingVertical="$2"
              alignItems="center"
              gap="$2"
              borderRadius={12}
              overflow="hidden"
              style={{
                // Glass morphism effect
                backgroundColor: Platform.OS === 'web' ? 'rgba(200, 162, 200, 0.15)' : 'rgba(200, 162, 200, 0.25)',
                ...(Platform.OS === 'web' && {
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                }),
                borderWidth: 1,
                borderColor: 'rgba(255, 255, 255, 0.2)',
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: 0.1,
                shadowRadius: 4,
                maxHeight: Platform.OS === 'web' ? 60 : 56,
                minHeight: Platform.OS === 'web' ? 60 : 56,
              }}
            >
              {/* Gradient Background Layer */}
              {Platform.OS === 'web' ? (
                <View
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(135deg, rgba(200, 162, 200, 0.15) 0%, rgba(135, 206, 235, 0.15) 100%)',
                    zIndex: 0,
                  }}
                />
              ) : (
                <LinearGradient
                  colors={['rgba(200, 162, 200, 0.15)', 'rgba(135, 206, 235, 0.15)']}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 1 }}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 0,
                  }}
                />
              )}

              {/* Icon Container - Smaller */}
              <View style={{
                width: Platform.OS === 'web' ? 40 : 36,
                height: Platform.OS === 'web' ? 40 : 36,
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                borderRadius: Platform.OS === 'web' ? 20 : 18,
                justifyContent: 'center',
                alignItems: 'center',
                overflow: 'hidden',
                borderWidth: 1.5,
                borderColor: 'rgba(255, 255, 255, 0.3)',
                zIndex: 1,
                flexShrink: 0,
              }}>
                <Image
                  key={`${integration.meta}-${integration.title}-disconnected`}
                  source={sources}
                  contentFit='contain'
                  style={{ width: Platform.OS === 'web' ? 32 : 28, height: Platform.OS === 'web' ? 32 : 28 }}
                />
              </View>

              {/* Title and Description - Compact with better text handling */}
              <YStack flex={1} zIndex={1} gap="$1" justifyContent="center">
                <H4
                  color="rgba(255,255,255,0.95)"
                  fontWeight="600"
                  fontSize={Platform.OS === 'web' ? "$5" : "$4"}
                  numberOfLines={1}
                  ellipsizeMode="tail"
                >
                  {integration.title}
                </H4>
              </YStack>
            </XStack>

            {/* Content Section */}
            <ScrollView
              style={{ flex: 1 }}
              contentContainerStyle={{
                padding: Platform.OS === 'web' ? 16 : 12,
                gap: Platform.OS === 'web' ? 16 : 12,
              }}
              showsVerticalScrollIndicator={false}
            >
              {/* Spec-driven rendering when enriched OpenAPI is present (even if not connected yet) */}
              {(integration as any).openAPISpec?.enrichedSpec && (
                <ErrorBoundary>
                  <SpecDrivenIntegrationRenderer
                    integrationId={(integration as any)._id || (integration as any).id || integration.meta}
                  />
                </ErrorBoundary>
              )}

              {/* Integration Setup */}
              <YStack
                backgroundColor={colors.cardBackground}
                borderRadius={12}
                borderWidth={1}
                borderColor={colors.border}
                padding="$4"
                gap="$3"
              >
                {integration.meta === 'wordpress' && (
                  <YStack flex={1} gap="$3">
                    <H4
                      color={colors.text}
                      fontWeight="600"
                      fontSize={Platform.OS === 'web' ? "$5" : "$4"}
                    >
                      Connect WordPress
                    </H4>
                    <SizableText
                      color={colors.textSecondary}
                      size={Platform.OS === 'web' ? "$3" : "$2"}
                      lineHeight={Platform.OS === 'web' ? undefined : 18}
                    >
                      Enter your WordPress site details to connect
                    </SizableText>
                    <GenerateSchemaForm
                      submitFunction={async (vals) => {
                        await authCallback(vals, integration.meta, ['read', 'write'], "").then((res) => {
                          dispatch({ type: ADD_INTEGRATION, payload: { authType: integration.meta, data: res.data } })
                          dispatch({ type: HIDE_LOADING_MODAL });
                          dispatch({ type: SHOW_AUTH_MODAL, data: { content: res.message, type: 'success' } });
                        }).then(() => {
                          queryClient.invalidateQueries({ queryKey: ['integrationData'] });
                          queryClient.refetchQueries({ queryKey: ['integrationData'] });
                        })
                      }}
                      customStyling={{
                        minWidth: "100%",
                        flex: 1,
                        maxWidth: "100%",
                      }}
                      scrollEnabled={false}
                      schema={[
                        { title: "url", content: { label: "WordPress URL" }, type: "text" },
                        { title: "id", content: { label: "User name" }, type: "text" },
                        { title: "password", type: "password" }
                      ]}
                      buttonLabel='Connect'
                    />
                  </YStack>
                )}

                {integration.meta === 'yodeck' && (
                  <YStack flex={1} gap="$3">
                    <H4
                      color={colors.text}
                      fontWeight="600"
                      fontSize={Platform.OS === 'web' ? "$5" : "$4"}
                    >
                      Connect Yodeck
                    </H4>
                    <SizableText
                      color={colors.textSecondary}
                      size={Platform.OS === 'web' ? "$3" : "$2"}
                      lineHeight={Platform.OS === 'web' ? undefined : 18}
                    >
                      Enter your Yodeck API token to connect. You can generate an API token from your Yodeck Account Settings → API Tokens.
                    </SizableText>
                    <View style={{ minHeight: 150, width: '100%' }}>
                      <GenerateSchemaForm
                        submitFunction={async (vals) => {
                          dispatch({ type: SHOW_LOADING_MODAL });
                          await authCallback(vals, integration.meta, [], "").then((res) => {
                            dispatch({ type: ADD_INTEGRATION, payload: { authType: integration.meta, data: res.data } })
                            dispatch({ type: HIDE_LOADING_MODAL });
                            dispatch({ type: SHOW_AUTH_MODAL, data: { content: res.message, type: 'success' } });
                          }).catch((error) => {
                            dispatch({ type: HIDE_LOADING_MODAL });
                            dispatch({ type: SHOW_AUTH_MODAL, data: { content: `Failed to connect Yodeck: ${error.message}`, type: 'error' } });
                          }).then(() => {
                            queryClient.invalidateQueries({ queryKey: ['integrationData'] });
                            queryClient.refetchQueries({ queryKey: ['integrationData'] });
                          })
                        }}
                        customStyling={{
                          minWidth: "100%",
                          width: "100%",
                          maxWidth: "100%",
                        }}
                        scrollEnabled={false}
                        schema={[
                          { title: "apiToken", content: { label: "API Token" }, type: "password" }
                        ]}
                        buttonLabel='Connect'
                      />
                    </View>
                  </YStack>
                )}

                {(integration.meta === 'huggingface' || integration.meta === 'hugging-face') && (
                  <ErrorBoundary>
                    {getOptionList(integration).find(item => {
                      const normalizedMeta = integration.meta?.toLowerCase().replace('-', '');
                      const normalizedTitle = item.title?.toLowerCase();
                      return normalizedTitle === normalizedMeta;
                    })?.component || null}
                  </ErrorBoundary>
                )}

                {(integration.meta === 'paystack' || integration.meta === 'pay-stack') && (
                  <ErrorBoundary>
                    {getOptionList(integration).find(item => {
                      const normalizedMeta = integration.meta?.toLowerCase().replace('-', '');
                      const normalizedTitle = item.title?.toLowerCase();
                      return normalizedTitle === normalizedMeta;
                    })?.component || null}
                  </ErrorBoundary>
                )}

                {integration.meta === 'lovable' && (
                  <YStack gap="$3" alignItems="center">
                    <MaterialCommunityIcons
                      name="code-tags"
                      size={Platform.OS === 'web' ? 48 : 40}
                      color={colors.smartBlue}
                    />
                    <H4
                      color={colors.text}
                      textAlign="center"
                      fontSize={Platform.OS === 'web' ? "$5" : "$4"}
                    >
                      Connect Lovable
                    </H4>
                    <SizableText
                      color={colors.textSecondary}
                      size={Platform.OS === 'web' ? "$3" : "$2"}
                      textAlign="center"
                      lineHeight={Platform.OS === 'web' ? undefined : 18}
                    >
                      Connect your Lovable integration to automatically sync deployment information and app data to SmartSaaS. Once connected, you'll see detailed instructions on how to configure your deployment hooks.
                    </SizableText>
                    <Button
                      onPress={async () => {
                        try {
                          dispatch({ type: SHOW_LOADING_MODAL });
                          // Mark as connected - Lovable doesn't require authentication
                          await authCallback({ connected: true }, integration.meta, [], "").then((res) => {
                            dispatch({ type: ADD_INTEGRATION, payload: { authType: integration.meta, data: res.data || { connected: true } } });
                            dispatch({ type: HIDE_LOADING_MODAL });
                            dispatch({ type: SHOW_AUTH_MODAL, data: { content: 'Lovable integration connected successfully!', type: 'success' } });
                          }).catch((error) => {
                            // If backend doesn't support this, just mark it locally
                            dispatch({ type: ADD_INTEGRATION, payload: { authType: integration.meta, data: { connected: true } } });
                            dispatch({ type: HIDE_LOADING_MODAL });
                            dispatch({ type: SHOW_AUTH_MODAL, data: { content: 'Lovable integration connected!', type: 'success' } });
                          }).then(() => {
                            queryClient.invalidateQueries({ queryKey: ['integrationData'] });
                            queryClient.refetchQueries({ queryKey: ['integrationData'] });
                          });
                        } catch (e) {
                          dispatch({ type: HIDE_LOADING_MODAL });
                          dispatch({ type: SHOW_AUTH_MODAL, data: { content: `Failed to connect Lovable: ${e}`, type: "error" } });
                        }
                      }}
                      defaultColor={integration.themeColor || colors.smartBlue}
                      buttonText={'Connect Lovable'}
                      textColor='white'
                      customStyle={{
                        minWidth: 200,
                        borderRadius: 8,
                        shadowRadius: 0,
                        shadowOpacity: 0,
                        paddingVertical: 12
                      }}
                    />
                  </YStack>
                )}

                {integration.meta === 'whatsapp' && (
                  <YStack gap="$3" alignItems="center">
                    <MaterialCommunityIcons
                      name="whatsapp"
                      size={Platform.OS === 'web' ? 48 : 40}
                      color={colors.success}
                    />
                    <H4
                      color={colors.text}
                      textAlign="center"
                      fontSize={Platform.OS === 'web' ? "$5" : "$4"}
                    >
                      Connect WhatsApp Business
                    </H4>
                    <SizableText
                      color={colors.textSecondary}
                      size={Platform.OS === 'web' ? "$3" : "$2"}
                      textAlign="center"
                      lineHeight={Platform.OS === 'web' ? undefined : 18}
                    >
                      Connect your WhatsApp Business account to start messaging
                    </SizableText>
                    <Button
                      onPress={launchWhatsAppSignup}
                      defaultColor={integration.themeColor || colors.smartBlue}
                      buttonText={'Connect WhatsApp'}
                      textColor='white'
                      customStyle={{
                        minWidth: 200,
                        borderRadius: 8,
                        shadowRadius: 0,
                        shadowOpacity: 0,
                        paddingVertical: 12
                      }}
                    />
                  </YStack>
                )}

                {(integration.meta === 'facebook' || integration.meta === 'instagram') && (
                  <YStack gap="$3" alignItems="center">
                    <MaterialCommunityIcons
                      name={integration.meta === 'facebook' ? "facebook" : "instagram"}
                      size={Platform.OS === 'web' ? 48 : 40}
                      color={integration.meta === 'facebook' ? '#1877f2' : '#E4405F'}
                    />
                    <H4
                      color={colors.text}
                      textAlign="center"
                      fontSize={Platform.OS === 'web' ? "$5" : "$4"}
                    >
                      Connect {integration.title}
                    </H4>
                    <SizableText
                      color={colors.textSecondary}
                      size={Platform.OS === 'web' ? "$3" : "$2"}
                      textAlign="center"
                      lineHeight={Platform.OS === 'web' ? undefined : 18}
                    >
                      Connect your {integration.title} account to manage your content
                    </SizableText>
                    <Button
                      buttonText={`Connect ${integration.title}`}
                      textColor='white'
                      defaultColor={integration.meta === 'facebook' ? '#1877f2' : '#E4405F'}
                      onPress={async () => {
                        promptAsync({ windowFeatures: { dependant: true, name: 'auth' } })
                      }}
                      customStyle={{
                        minWidth: 200,
                        borderRadius: 8,
                        shadowRadius: 0,
                        shadowOpacity: 0,
                        paddingVertical: 12
                      }}
                    >
                      Connect {integration.title}
                    </Button>
                  </YStack>
                )}

                {!excludeList.includes(integration.meta) && integration.endPoint && !loading && !isHuggingFace && (
                  <YStack gap="$3" alignItems="center">
                    <MaterialCommunityIcons
                      name="link-variant"
                      size={Platform.OS === 'web' ? 48 : 40}
                      color={colors.accent}
                    />
                    <H4
                      color={colors.text}
                      textAlign="center"
                      fontSize={Platform.OS === 'web' ? "$5" : "$4"}
                    >
                      Connect {integration.title}
                    </H4>
                    <SizableText
                      color={colors.textSecondary}
                      size={Platform.OS === 'web' ? "$3" : "$2"}
                      textAlign="center"
                      lineHeight={Platform.OS === 'web' ? undefined : 18}
                    >
                      {integration.description}
                    </SizableText>
                    <Button
                      onPress={async () => {
                        try {
                          await debugPromptAsync({ windowFeatures: { dependant: true, name: 'auth' } })
                        } catch (e) {
                          dispatch({ type: SHOW_AUTH_MODAL, data: { content: `${e}`, type: "error" } })
                        }
                      }}
                      defaultColor={integration.themeColor || colors.smartBlue}
                      buttonText={`Connect ${integration.title}`}
                      textColor='white'
                      customStyle={{
                        minWidth: 200,
                        borderRadius: 8,
                        shadowRadius: 0,
                        shadowOpacity: 0,
                        paddingVertical: 12
                      }}
                    />
                  </YStack>
                )}

                {loading && (
                  <YStack gap="$3" alignItems="center" justifyContent="center" flex={1}>
                    <Spinner size={Platform.OS === 'web' ? "large" : "small"} color={colors.smartBlue} />
                    <SizableText
                      color={colors.textSecondary}
                      textAlign="center"
                      size={Platform.OS === 'web' ? "$4" : "$3"}
                      lineHeight={Platform.OS === 'web' ? undefined : 18}
                    >
                      Loading integration...
                    </SizableText>
                  </YStack>
                )}

                {!integration.endPoint && !excludeList.includes(integration.meta) && !isHuggingFace && !isPayStack && !(integration as any).openAPISpec?.enrichedSpec && (
                  <YStack gap="$3" alignItems="center" justifyContent="center" flex={1}>
                    <MaterialCommunityIcons
                      name="clock-outline"
                      size={Platform.OS === 'web' ? 48 : 40}
                      color={colors.textSecondary}
                    />
                    <H4
                      color={colors.text}
                      textAlign="center"
                      fontSize={Platform.OS === 'web' ? "$5" : "$4"}
                    >
                      Coming Soon
                    </H4>
                    <SizableText
                      color={colors.textSecondary}
                      size={Platform.OS === 'web' ? "$3" : "$2"}
                      textAlign="center"
                      lineHeight={Platform.OS === 'web' ? undefined : 18}
                    >
                      This integration will be available soon
                    </SizableText>
                  </YStack>
                )}
              </YStack>
            </ScrollView>
          </YStack>
      }
    </YStack>
  )
}
export default IntegrationComponent;